import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useStore } from '@hub/helpers/use-store';
import { LoadingState, useLoading } from '@monorepo/react-hooks';
import { AccountStats } from '@monorepo/types';
import AdvancedStatisticsCard, {
  type AdvancedStatisticsType,
} from './advanced-statistics-card';

dayjs.extend(duration);

const formatDuration = (ms: number): string => {
  const durationObj = dayjs.duration(ms);
  const hours = durationObj.hours();
  const minutes = durationObj.minutes();
  const seconds = durationObj.seconds();

  return `${hours}h:${minutes}m:${seconds}s`;
};

interface Props {
  accountId: string;
  stats?: AccountStats;
  renderView?: 'row' | 'cards';
}

const AdvancedStatistics: FunctionComponent<Props> = ({
  accountId,
  stats,
  renderView,
}) => {
  const {
    dataStore: { accountStore },
  } = useStore();
  const { t } = useTranslation(['dashboardTemplates']);
  const [accountStats, setAccountStats] = useState<AccountStats>({});
  const { updateLoadingState, isLoading } = useLoading();

  const leftColumn: AdvancedStatisticsType[] = [
    {
      title: t('dashboardTemplates:total_trades'),
      value: accountStats.totalTrades || 0,
      tooltipText: t('dashboardTemplates:total_trades_tooltip'),
    },
    {
      title: t('dashboardTemplates:profit_trades'),
      value: accountStats.profitTrades || 0,
      tooltipText: t('dashboardTemplates:profit_trades_tooltip'),
    },
    {
      title: t('dashboardTemplates:loss_trades'),
      value: accountStats.lossTrades || 0,
      tooltipText: t('dashboardTemplates:loss_trades_tooltip'),
    },
    {
      title: t('dashboardTemplates:long_trades'),
      value: accountStats.longTrades || 0,
      tooltipText: t('dashboardTemplates:long_trades_tooltip'),
    },
    {
      title: t('dashboardTemplates:short_trades'),
      value: accountStats.shortTrades || 0,
      tooltipText: t('dashboardTemplates:short_trades_tooltip'),
    },
    {
      title: t('dashboardTemplates:gross_profit'),
      value: accountStats.grossProfit || 0,
      tooltipText: t('dashboardTemplates:gross_profit_tooltip'),
      valuePrefix: '$',
      valuePrecision: 2,
    },
    {
      title: t('dashboardTemplates:gross_loss'),
      value: accountStats.grossLoss || 0,
      tooltipText: t('dashboardTemplates:gross_loss_tooltip'),
      valuePrefix: '$',
      valuePrecision: 2,
    },
    {
      title: t('dashboardTemplates:total_net_profit'),
      value: accountStats.totalNetProfit || 0,
      tooltipText: t('dashboardTemplates:total_net_profit_tooltip'),
      valuePrefix: '$',
      valuePrecision: 2,
    },
    {
      title: t('dashboardTemplates:profit_factor'),
      value: accountStats.profitFactor || 0,
      valuePrecision: 2,
      tooltipText: t('dashboardTemplates:profit_factor_tooltip'),
    },
    {
      title: t('dashboardTemplates:win_rate'),
      value: (accountStats.winRate || 0) * 100,
      tooltipText: t('dashboardTemplates:win_rate_tooltip'),
      valuePrecision: 2,
      valueSuffix: '%',
    },
  ];

  const rightColumn: AdvancedStatisticsType[] = [
    {
      title: t('dashboardTemplates:best_profit'),
      value: Math.max(0, accountStats.bestProfit || 0),
      tooltipText: t('dashboardTemplates:best_profit_tooltip'),
      valuePrefix: '$',
      valuePrecision: 2,
    },
    {
      title: t('dashboardTemplates:biggest_loss'),
      value: Math.min(0, accountStats.biggestLoss || 0),
      tooltipText: t('dashboardTemplates:biggest_loss_tooltip'),
      valuePrefix: '$',
      valuePrecision: 2,
    },
    {
      title: t('dashboardTemplates:avg_profit'),
      value: accountStats.avgProfit || 0,
      tooltipText: t('dashboardTemplates:avg_profit_tooltip'),
      valuePrecision: 2,
      valuePrefix: '$',
    },
    {
      title: t('dashboardTemplates:avg_loss'),
      value: accountStats.avgLoss || 0,
      tooltipText: t('dashboardTemplates:avg_loss_tooltip'),
      valuePrecision: 2,
      valuePrefix: '$',
    },
    {
      title: t('dashboardTemplates:expectancy'),
      value: accountStats.expectancy || 0,
      tooltipText: t('dashboardTemplates:expectancy_tooltip'),
      valuePrefix: '$',
      valuePrecision: 2,
    },
    {
      title: t('dashboardTemplates:avg_trade_size'),
      value: accountStats.avgTradeSize || 0,
      tooltipText: t('dashboardTemplates:avg_trade_size_tooltip'),
      valuePrecision: 2,
    },
    {
      title: t('dashboardTemplates:avg_trade_duration'),
      value: formatDuration(accountStats.avgTradeDuration || 0),
      tooltipText: t('dashboardTemplates:avg_trade_duration_tooltip'),
    },
  ];

  useEffect(() => {
    const init = async () => {
      try {
        if (stats) {
          setAccountStats(stats);
          return;
        }

        updateLoadingState(LoadingState.Loading);
        const result = await accountStore.getStats(accountId);

        setAccountStats(result);
      } catch (e) {
        console.error(`Failed getting account stats`, e);
      } finally {
        updateLoadingState(LoadingState.Loaded);
      }
    };

    init();
  }, [accountId, stats]);

  return (
    <AdvancedStatisticsCard
      renderView={renderView}
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      isLoading={isLoading}
    />
  );
};

export default AdvancedStatistics;
